import React from 'react';

import { useMutation, useApolloClient } from '@apollo/client';
import { QUERY, MUTATION } from '../gql-operations';

import { AuthContext } from '../util/context';
import { CacheManager } from '../util/cacheManager';
import customAnalytics from '../util/custom-analytics';

/**
 * Hook for trashing messages (soft delete - move to trash)
 * Uses centralized CacheManager for cache updates
 */
function useTrashMessagesQuery() {
    const client = useApolloClient();
    const [trashMessages, { data: successResponse }] = useMutation(MUTATION.TRASH_MESSAGES);

    const {
        state: {
            folderFilterType,
            selectedFolder,
            showDeletedMessages,
            selectedMessage,
            searchTerm,
            searchGlobal,
            searchDeletedMessages,
            searchActive,
            searchFolderFilterType
        },
        context: { setSelectedMessage }
    } = React.useContext(AuthContext);
    const normalizedSearchTerm = String(searchTerm || '').trim();

    const storeOptimisticTrashMessages = React.useCallback(messages => {
        if (typeof window === 'undefined' || !Array.isArray(messages) || messages.length === 0) {
            return;
        }

        const existingMessages = window.__optimisticTrashMessages instanceof Map
            ? window.__optimisticTrashMessages
            : new Map();
        const trashedAt = new Date().toISOString();

        messages.forEach(message => {
            const messageId = String(message?._canonicalId || message?.id || '').trim();
            if (!messageId) {
                return;
            }

            existingMessages.set(messageId, {
                ...message,
                id: messageId,
                deletedAt: message?.deletedAt || trashedAt,
                _originalId: message?._originalId || message?.id || messageId,
                _canonicalId: messageId
            });
        });

        window.__optimisticTrashMessages = existingMessages;
    }, []);

    const resolveMessageIdFromCache = React.useCallback((clientRequestId) => {
        const normalizedClientRequestId = String(clientRequestId || '').trim();
        if (!normalizedClientRequestId) {
            return '';
        }

        const cacheData = client.cache.extract();
        for (const entry of Object.values(cacheData)) {
            if (!entry || typeof entry !== 'object') {
                continue;
            }

            if (entry.__typename !== 'Message') {
                continue;
            }

            if (String(entry.clientRequestId || '').trim() !== normalizedClientRequestId) {
                continue;
            }

            return String(entry.id || '').trim();
        }

        return '';
    }, [client]);

    const resolveCanonicalMessageId = React.useCallback((message) => {
        const resolvedServerId = String(
            message?._resolvedServerId || message?.rawMessage?.id || ''
        ).trim();
        if (resolvedServerId) {
            return resolvedServerId;
        }

        const currentId = String(message?.id || '').trim();
        if (currentId && !currentId.startsWith('temp-')) {
            return currentId;
        }

        const clientRequestId = String(
            message?.clientRequestId || message?.rawMessage?.clientRequestId || ''
        ).trim();

        if (clientRequestId) {
            return resolveMessageIdFromCache(clientRequestId);
        }

        return '';
    }, [resolveMessageIdFromCache]);

    const normalizeMessagesForMutation = React.useCallback((messages) => {
        return messages.map(message => {
            const originalId = String(message?.id || '').trim();
            const canonicalId = resolveCanonicalMessageId(message);

            return {
                ...message,
                _originalId: originalId,
                _canonicalId: canonicalId,
                id: canonicalId || originalId
            };
        });
    }, [resolveCanonicalMessageId]);

    /**
     * Close message preview if trashed message is currently selected
     */
    const closeMessagePreview = message => {
        if (!selectedMessage) {
            return;
        }

        const selectedId = String(selectedMessage.id || '').trim();
        const messageId = String(message.id || '').trim();
        const messageOriginalId = String(message._originalId || '').trim();
        const messageCanonicalId = String(message._canonicalId || '').trim();

        if (
            selectedId &&
            (
                selectedId === messageId ||
                selectedId === messageOriginalId ||
                selectedId === messageCanonicalId
            )
        ) {
            setSelectedMessage(null);
        }
    };

    /**
     * Extract message IDs for the mutation
     * Filters out temp/optimistic messages that don't exist on server yet
     */
    const prepareMessages = messages => {
        return messages
            .map(message => String(message?._canonicalId || message?.id || '').trim())
            .filter(Boolean)
            .filter(messageId => !messageId.startsWith('temp-'))
            ;
    };

    /**
     * Perform cache update for message trashing
     */
    const performCacheUpdate = (messages, { emitTrashEvent = true } = {}) => {
        // Close preview for any trashed messages
        messages.forEach(message => {
            closeMessagePreview(message);
        });

        // Determine the correct isTrash value for cache operations
        // When trashing, we're removing from the current (non-trash) view
        const isTrash = selectedFolder?.deletedAt ? true : showDeletedMessages;

        // Use CacheManager to remove messages from all caches
        // (Trashing removes from current view, doesn't add to trash view immediately
        //  - that happens on next query to trash)
        CacheManager.removeMessages(client, messages, {
            folderId: selectedFolder?.id,
            isTrash: isTrash,
            filterType: folderFilterType,
            isSearch: searchActive,
            searchTerm: normalizedSearchTerm,
            searchGlobal: searchGlobal,
            searchFolderFilterType: searchFolderFilterType
        });

        // Dispatch event to update memory refs in FolderContainer
        const messageIds = Array.from(new Set(
            messages.flatMap(message => [
                message?._originalId,
                message?._canonicalId,
                message?.id
            ].filter(Boolean))
        ));
        if (emitTrashEvent) {
            storeOptimisticTrashMessages(messages);
            CacheManager.dispatchEvent('messagesTrashed', {
                folderId: selectedFolder?.id,
                messageIds,
                messages,
                memoryKey: `${selectedFolder?.id}_${isTrash ? 'trash' : 'normal'}`
            });
        }
    };

    /**
     * Main function to trash messages
     */
    const runThis = (messages, fromFolder) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('🗑️ useTrashMessages: Trashing', messages.length, 'messages');
        }

        const normalizedMessages = normalizeMessagesForMutation(messages);

        // Optimistically update cache IMMEDIATELY for instant UI feedback
        try {
            performCacheUpdate(normalizedMessages, { emitTrashEvent: true });
        } catch (optimisticError) {
            if (process.env.NODE_ENV === 'development') {
                console.log('Optimistic update failed, will rely on mutation update callback:', optimisticError);
            }
        }

        const messageIds = prepareMessages(normalizedMessages);
        
        // If all messages were temp messages (filtered out), nothing to send to server
        if (messageIds.length === 0) {
            if (process.env.NODE_ENV === 'development') {
                console.log('🗑️ useTrashMessages: All messages are temp, skipping server mutation');
            }
            return;
        }
        
        // Now call the mutation
        trashMessages({
            variables: {
                data: {
                    messageIds: messageIds
                }
            },
            update: (store, { data: { trashMessages } }) => {
                try {
                    // Run cache update again to ensure consistency after server response
                    // Optimistic pass already removed rows and emitted UI events.
                    // Reconcile cache silently to avoid duplicate event churn/jank.
                    performCacheUpdate(normalizedMessages, { emitTrashEvent: false });
                    
                    if (process.env.NODE_ENV === 'development') {
                        console.log('✅ Trash mutation completed, cache updated');
                    }
                } catch (error) {
                    if (process.env.NODE_ENV === 'development') {
                        console.error('Cache update failed during trash:', error);
                    }
                }
            },
            onError: (error) => {
                // If mutation fails, log error
                if (process.env.NODE_ENV === 'development') {
                    console.error('Trash mutation failed:', error);
                }
            }
        });

        customAnalytics.trash_messages(normalizedMessages);
    };

    return [runThis, { successResponse }];
}

export default useTrashMessagesQuery;
